import { Component, Inject, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import { AddReminderModalComponent } from '../../../shared/reminders/components/add-reminder-modal/add-reminder-modal.component';
import { BackendApiService } from '../../../../services/backend-api.service';
import { ToasterService } from '../../../../services/core/toaster.service';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { AddTaskComponent } from '../add-task/add-task.component';
import { DialogService } from '../../../../services/core/dialog.service';
import { ConfirmationPopupComponent } from '../../../shared/confirmation-popup/confirmation-popup.component';
import { ObserverParticipantPopupComponent } from '../add-task/add-checklist/observer-participant-popup/observer-participant-popup.component';
import { ExportPdfSharedComponent } from '../../../shared/export-pdf/export-pdf.component';
import { getBackgroundColor } from 'src/app/utils/utils.function';
import { environment } from 'src/environments/environment';
import { HelperService } from 'src/app/services/helper.service';
import { JsonService } from 'src/app/services/core/json.service';
import { DocumentService } from 'src/app/modules/document/document.service';
import { TaskTag } from '../add-task/task-sidebar/task-sidebar.component';
import { TaskTagSelectorPopupComponent } from '../add-task/task-tag-selector-popup/task-tag-selector-popup.component';
import { formatDate } from '@angular/common';
import { ProposalUnlockComponent } from '../../proposal/edit-proposal/proposal-unlock/proposal-unlock.component';
import { CopyTaskDialogComponent } from '../copy-task-dialog/copy-task-dialog.component';
import { MODULE_IDS } from 'src/app/constants/module-ids.constant';

@Component({
  selector: 'app-view-task-detail',
  templateUrl: './view-task-detail.component.html',
  styleUrls: ['./view-task-detail.component.scss'],
  standalone: false,
})
export class ViewTaskDetailComponent implements OnInit {
  public taskID: any;
  model: any = '';
  /** True until the task has been fetched, so the panel shows a loader instead of empty placeholders. */
  isLoading = true;
  public TASK_MODULE_ID: number = 7;
  stages: any = [];
  statuses: any = [];
  loadListingOnClose = false;
  public is_activity_log: any = 1;
  public CONST_RESPONSIBLE_PERSON_TYPE: string = 'responsible_person';
  public CONST_CREATED_BY_TYPE: string = 'created_by';
  public CONST_OBSERVER_TYPE: string = 'observer';
  public CONST_PARTICIPANT_TYPE: string = 'participant';

  isLocked = false;
  // Permissions
  createTaskPermission = 'DONOTAPPLY';
  editTaskPermission = 'DONOTAPPLY';
  deleteTaskPermission = 'DONOTAPPLY';
  changeStatusTaskPermission = 'DONOTAPPLY';
  changePriorityTaskPermission = 'DONOTAPPLY';
  exportPDFPermission = 'DONOTAPPLY';
  changeStageTaskPermission = 'DONOTAPPLY';

  // UI State for new components
  isSidebarCollapsed = false;
  isFullscreen = false;
  isSearchOpen = false;
  searchQuery = '';
  activeTabIndex = 0;
  taskHistory: any[] = [];
  isFavorite = false;

  // Additional data properties
  milestones: any[] = [];
  modulesList: any = [];
  selectedItem: any = {};
  taskTags: TaskTag[] = [];
  taskComments: any[] = []; // Comments for comment count badge

  public sectionForm: UntypedFormGroup = new UntypedFormGroup({
    description: new UntypedFormControl(''),
    status: new UntypedFormControl(null),
    priority: new UntypedFormControl(1),
    // Date-related controls
    due_date: new UntypedFormControl(null),
    start_date: new UntypedFormControl(null),
    date_finished: new UntypedFormControl(null),
    duration: new UntypedFormControl(null),
    is_time_planning: new UntypedFormControl(false),
  });
  constructor(
    public dialogRef: MatDialogRef<ViewTaskDetailComponent>,
    public backendApiService: BackendApiService,
    public toasterService: ToasterService,
    public dialog: NgDialogAnimationService,
    public dialogService: DialogService,
    private helperService: HelperService,
    public jsonService: JsonService,
    private documentService: DocumentService,
    private matDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.taskID = this.data?.id;
    this.setPermissions(this.data?.permissions);
    // Callers can land the popup on a specific tab (e.g. the dashboard's
    // Comments/Attachments buttons). Defaults to Details.
    if (typeof this.data?.activeTabIndex === 'number') {
      this.activeTabIndex = this.data.activeTabIndex;
    }
  }

  ngOnInit(): void {
    this.getTaskByID(true);
    this.getTaskStatuses();
    this.getModules();
    // this.getStagesOfModule(this.TASK_MODULE_ID);
  }

  getTaskByID(isInitialLoad: boolean = false) {
    const params =
      isInitialLoad || this.is_activity_log
        ? { is_activity_log: this.is_activity_log }
        : {};
    if (isInitialLoad) {
      this.isLoading = true;
    }
    this.backendApiService
      .getApi(`tasks/${this.taskID}`, params)
      .subscribe({
        next: (response: any) => {
        this.isLoading = false;
        this.model = response.data;
        // Initialize lock state from API response
        this.isLocked = this.model?.is_locked ? true : false;
        // Initialize favorite state from API response
        this.isFavorite = !!this.model?.is_favorite;

        // Initialize empty taskHistory - the task-history-tab component will fetch its own data
        this.taskHistory = [];

        // Extract and transform tags from model
        this.taskTags = this.extractTagsFromModel(this.model);

        // Populate form with task data for description display
        this.sectionForm.patchValue({
          description: this.model?.description || '',
          status: this.model?.status_label?.id || null,
          priority: this.model?.priority || 1,
          // Patch date fields - ensure yyyy-MM-dd format for HTML date inputs
          due_date: this.model?.due_date
            ? formatDate(this.model.due_date, 'yyyy-MM-dd', 'en-US')
            : null,
          start_date: this.model?.start_date
            ? formatDate(this.model.start_date, 'yyyy-MM-dd', 'en-US')
            : null,
          date_finished: this.model?.date_finished
            ? formatDate(this.model.date_finished, 'yyyy-MM-dd', 'en-US')
            : null,
          duration: this.model?.duration || null,
          is_time_planning: this.model?.is_time_planning || false,
        });

        // Debug log to verify date values
        console.log('[VIEW-TASK-DETAIL] Date values:', {
          due_date: this.model?.due_date,
          start_date: this.model?.start_date,
          date_finished: this.model?.date_finished,
          formatted_due_date: this.model?.due_date
            ? formatDate(this.model.due_date, 'yyyy-MM-dd', 'en-US')
            : null,
        });

        // Calculate initial completion percentage based on current task state
        this.completionPercentage = this.calculateCompletionPercentage();

        this.getStagesOfModule(this.model?.module?.id);

        // Load milestones if we have both module and reference
        if (this.model?.module?.id && this.model?.reference?.id) {
          this.loadMilestones();
        }

        // Set selected item from reference
        if (this.model?.reference) {
          this.selectedItem = this.model.reference;
        }

        // Load comments for count badge
        this.getTaskComments();
        },
        // Never leave the loader up if the task could not be fetched.
        error: () => {
          this.isLoading = false;
        },
      });
    this.is_activity_log = 0;
  }

  getStagesOfModule(id: any) {
    this.backendApiService
      .getApi('stages?module_id=' + id)
      .subscribe((response: any) => {
        this.stages = response.data;
      });
  }

  editTask() {
    if (this.isLocked) {
      return;
    }
    console.log('[VIEW-TASK-DETAIL] editTask() called');
    console.log('[VIEW-TASK-DETAIL] Model being passed:', this.model);
    console.log('[VIEW-TASK-DETAIL] Has name?:', !!this.model?.name);
    console.log(
      '[VIEW-TASK-DETAIL] Has description?:',
      !!this.model?.description,
    );
    console.log(
      '[VIEW-TASK-DETAIL] Model structure:',
      JSON.stringify(this.model, null, 2),
    );
    this.close();
    this.dialogService.onOpenFromRightDialog(
      AddTaskComponent,
      {
        isEdit: true,
        model: this.model,
        moduleID: this.model?.module?.id,
        id: this.model?.id,
      },
      1300,
    );
    this.dialogService.afterClose()?.subscribe((result: any) => {
      if (result) {
      }
    });
  }

  onConfirmation(): void {
    const dialogRef = this.dialog.open(ConfirmationPopupComponent, {
      width: '500px',
      height: '330px',
      data: {},
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onDelete();
      }
    });
  }

  onDelete() {
    if (this.isLocked) {
      return;
    }
    this.backendApiService
      .deleteApi(`tasks/${this.model?.id}`)
      .subscribe((response: any) => {
        this.toasterService.showSuccessMessage(response.data.message);
        this.close(true);
      });
  }

  openCopyTaskDialog(): void {
    if (this.isLocked) {
      return;
    }

    const dialogRef = this.dialog.open(CopyTaskDialogComponent, {
      width: '520px',
      maxHeight: '90vh',
      panelClass: 'copy-task-dialog-container',
      data: {
        taskId: this.taskID || this.model?.id,
        taskTitle: this.model?.name,
        moduleId: this.model?.module?.id,
        referenceId: this.model?.reference?.id ?? this.model?.reference_id,
        referenceName:
          this.model?.reference?.name ??
          this.model?.reference?.reference_number ??
          this.model?.reference?.title,
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result?.copied) {
        return;
      }

      this.loadListingOnClose = true;
      const newId = result?.newId;
      if (!newId) {
        this.getTaskByID();
        return;
      }
      this.taskID = newId;
      this.getTaskByID(true);
    });
  }

  close = (data: any = null) => {
    this.dialogRef.close(data);
  };

  createTask() {
    this.close();
    const moduleId = this.model?.module?.id;
    const referenceId = this.model?.reference?.id;
    const isTaskModule = moduleId & referenceId ? false : true;
    this.dialogService.onOpenFromRightDialog(
      AddTaskComponent,
      {
        isTaskModule: isTaskModule,
        moduleID: moduleId,
        referenceID: referenceId,
      },
      1300,
    );
    this.dialogService.afterClose()?.subscribe((result: any) => {
      if (result) {
      }
    });
  }

  addStage(stage: any) {
    this.backendApiService
      .postApi('tasks/update-stage/' + this.model?.id, { stage_id: stage?.id })
      .subscribe((response: any) => {
        this.toasterService.showSuccessMessage(response?.data?.message);
        this.getTaskByID(false);
      });
  }

  openUserPopup(event: any, type: any) {
    const position = event.target.getBoundingClientRect();
    const dialogRef = this.dialog.open(ObserverParticipantPopupComponent, {
      width: '420px',
      height: '500px',
      // position: {top:`${position.top+ 30}px`, left: `${position.left - 360}px`},
      data: {
        type: type,
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        if (result.type == this.CONST_RESPONSIBLE_PERSON_TYPE) {
          this.addResponsiblePerson(result?.user);
        } else if (result.type == this.CONST_OBSERVER_TYPE) {
          this.addObserver(result?.user);
        } else if (result.type == this.CONST_PARTICIPANT_TYPE) {
          this.addParticipant(result?.user);
        }
      }
    });
  }

  addResponsiblePerson(user: any) {
    const users = [];
    users.push(user.id);

    const usersList = this.model?.responsible_persons;
    const alreadyExistCheck = usersList.find((x: any) => x.id == user.id);
    if (!alreadyExistCheck) {
      this.backendApiService
        .postApi('tasks/add/responsible/person/' + this.model?.id, {
          user_id: users,
        })
        .subscribe((response: any) => {
          this.toasterService.showSuccessMessage(response?.data?.message);
          this.model?.responsible_persons?.push(response?.data?.data);
        });
    }
  }

  addObserver(user: any) {
    const users = [];
    users.push(user.id);

    const usersList = this.model?.observers;
    const alreadyExistCheck = usersList.find((x: any) => x.id == user.id);
    if (!alreadyExistCheck) {
      this.backendApiService
        .postApi('tasks/add/observer/' + this.model?.id, { user_id: users })
        .subscribe((response: any) => {
          this.toasterService.showSuccessMessage(response?.data?.message);
          this.model?.observers?.push(response?.data?.data);
        });
    }
  }

  addParticipant(user: any) {
    const users = [];
    users.push(user.id);

    const usersList = this.model?.participants;
    const alreadyExistCheck = usersList.find((x: any) => x.id == user.id);
    if (!alreadyExistCheck) {
      this.backendApiService
        .postApi('tasks/add/participant/' + this.model?.id, { user_id: users })
        .subscribe((response: any) => {
          this.toasterService.showSuccessMessage(response?.data?.message);
          this.model?.participants?.push(response?.data?.data);
        });
    }
  }

  deleteAssociation(user: any) {
    this.backendApiService
      .deleteApi('tasks/association/' + user?.assoc_id)
      .subscribe((response: any) => {
        this.toasterService.showSuccessMessage(response.data?.message);
        this.getTaskByID(false);
      });
  }

  getTaskStatuses() {
    this.backendApiService
      .getApi('statuses/task')
      .subscribe((response: any) => {
        this.statuses = response?.data;
      });
  }

  generatePDF() {
    const dialogRef = this.dialog.open(ExportPdfSharedComponent, {
      width: '30%',
      // height: '80%',
      data: {
        moduleName: 'task',
        moduleSlug: 'task',
        recordId: this.model.id,
        moduleId: this.TASK_MODULE_ID,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Dead code: `type: 3` can no longer be produced by the Export PDF dialog's
        // "Save to Documents" button (it now saves internally via a document-picker
        // flow and closes with {success:true}/void instead). Commented out instead
        // of deleted pending a full audit of this pattern across the app.
        // if (result.type == 3) {
        //   const userDetail = this.jsonService.doParse(
        //     localStorage.getItem('auth_detail'),
        //   );
        //   Object.values(result?.templates)?.map((templateValue: any) => {
        //     const url = `${environment.apiBaseUrl}/save-document?id=${this.model.id}&template_id=${templateValue}&output=2&user_id=${userDetail?.id}&module_id=${this.TASK_MODULE_ID}`;
        //     this.documentService.saveDocuments(url).subscribe(
        //       (res: any) => {},
        //       (error) => {
        //         this.toasterService.showError(
        //           'Error',
        //           'Internal Server Error!',
        //         );
        //       },
        //     );
        //   });
        // } else {
          this.helperService.getTemplates(
            environment.apiBaseUrl + '/task/generate-pdf',
            this.model?.id,
            result,
          );
        // }

        // this.toasterService.showWarning('PDF export not available.');
      }
    });
  }

  onChangeStatus(status: any) {
    this.backendApiService
      .postApi('tasks/change-status/' + this.model?.id, { status: status?.id })
      .subscribe((response: any) => {
        this.toasterService.showSuccessMessage(response?.data.message);
        this.loadListingOnClose = true;
        this.getTaskByID(false);
      });
  }

  /**
   * Toggle the task between High (3) and Medium (2) priority from the header
   * flame button. Refreshes in place — the panel must stay open.
   */
  togglePriority() {
    const priority = this.model?.priority === 3 ? 2 : 3;
    this.backendApiService
      .postApi(`tasks/update-priority/${this.model?.id}`, { priority })
      .subscribe((response: any) => {
        this.toasterService.showSuccessMessage(response?.data?.message);
        this.loadListingOnClose = true;
        this.getTaskByID(false);
      });
  }

  markAsComplete() {
    this.backendApiService
      .patchApi(`tasks/mark-completed/${this.model?.id}`, {})
      .subscribe(
        (response: any) => {
          this.toasterService.showSuccessMessage(
            response?.data?.message || 'Task marked as completed',
          );
          this.loadListingOnClose = true;
          this.getTaskByID(false);
        },
        (error: any) => {
          this.toasterService.showError(
            error?.error?.message || 'Failed to mark task as completed',
          );
        },
      );
  }

  getBackgroundColor(color: string) {
    return getBackgroundColor(color);
  }
  setPermissions(permissions: any) {
    if (permissions) {
      if (permissions?.isLocked !== undefined) {
        this.isLocked = permissions?.isLocked;
      }
      if (permissions?.createTaskPermission) {
        this.createTaskPermission = permissions?.createTaskPermission;
      }
      if (permissions?.editTaskPermission) {
        this.editTaskPermission = permissions?.editTaskPermission;
      }
      if (permissions?.changeStageTaskPermission) {
        this.changeStageTaskPermission = permissions?.changeStageTaskPermission;
      }
      if (permissions?.deleteTaskPermission) {
        this.deleteTaskPermission = permissions?.deleteTaskPermission;
      }
      if (permissions?.changeStatusTaskPermission) {
        this.changeStatusTaskPermission =
          permissions?.changeStatusTaskPermission;
      }
      if (permissions?.changePriorityTaskPermission) {
        this.changePriorityTaskPermission =
          permissions?.changePriorityTaskPermission;
      }
      if (permissions?.exportPDFPermission) {
        this.exportPDFPermission = permissions?.exportPDFPermission;
      }
    }
  }

  /**
   * Toggle lock/unlock status
   */
  toggleLock(): void {
    const dialogRefLock = this.dialog.open(ProposalUnlockComponent, {
      width: '500px',
      maxHeight: '80vh',
      panelClass: 'lock-dialog-container',
      data: {
        title: 'Task',
        model: this.model,
        is_locked: this.isLocked ? 0 : 1,
        url: `tasks/${this.model?.id}/toggle-unlock`,
      },
    });
    dialogRefLock.afterClosed().subscribe((result) => {
      if (result) {
        this.isLocked = !this.isLocked;
        this.model.is_locked = this.isLocked ? 1 : 0;
      }
    });
  }

  // ==========================================
  // NEW METHODS FOR UI/UX ENHANCEMENT
  // ==========================================

  /**
   * Load modules list for dropdown
   */
  getModules() {
    this.backendApiService
      .getApi('modules/type?type=task')
      .subscribe((response: any) => {
        this.modulesList = response?.data;
      });
  }

  /**
   * Load milestones for the current module and reference
   */
  loadMilestones() {
    if (!this.model?.module?.id || !this.model?.reference?.id) {
      return;
    }

    this.backendApiService
      .getApi(
        `module-data-milestones/${this.model.module.id}/${this.model.reference.id}`,
      )
      .subscribe(
        (response: any) => {
          if (response?.data && Array.isArray(response.data)) {
            this.milestones = response.data.map((m: any) =>
              this.transformMilestoneData(m),
            );
          } else {
            this.milestones = [];
          }
        },
        (error) => {
          console.error('Error loading milestones:', error);
          this.milestones = [];
        },
      );
  }

  /**
   * Transform API milestone data to match UI requirements
   */
  private transformMilestoneData(apiMilestone: any): any {
    const tasks = apiMilestone.tasks || [];
    const completedTasks = tasks.filter(
      (task: any) =>
        task.status === 'completed' ||
        task.status_name?.toLowerCase() === 'completed' ||
        task.completed_at !== null,
    );

    let status = apiMilestone.status;
    if (
      !status ||
      !['upcoming', 'in-progress', 'completed', 'overdue'].includes(status)
    ) {
      if (apiMilestone.completed_at) {
        status = 'completed';
      } else if (
        apiMilestone.due_date &&
        new Date(apiMilestone.due_date) < new Date()
      ) {
        status = 'overdue';
      } else if (apiMilestone.progress > 0 || completedTasks.length > 0) {
        status = 'in-progress';
      } else {
        status = 'upcoming';
      }
    }

    let progress = apiMilestone.progress;
    if (progress === undefined && tasks.length > 0) {
      progress = Math.round((completedTasks.length / tasks.length) * 100);
    }

    let daysRemaining = apiMilestone.days_left;
    if (daysRemaining === undefined && apiMilestone.due_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(apiMilestone.due_date);
      dueDate.setHours(0, 0, 0, 0);
      const diffTime = dueDate.getTime() - today.getTime();
      daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    return {
      id: apiMilestone.id,
      name:
        apiMilestone.milestone_name || apiMilestone.name || 'Unnamed Milestone',
      dueDate: apiMilestone.due_date || apiMilestone.dueDate,
      status: status,
      startDate: apiMilestone.start_date || apiMilestone.startDate,
      endDate: apiMilestone.end_date || apiMilestone.endDate,
      progress: progress,
      taskStats:
        tasks.length > 0
          ? {
              total: tasks.length,
              completed: completedTasks.length,
            }
          : undefined,
      daysRemaining: daysRemaining,
      description: apiMilestone.description,
      assignedTo: apiMilestone.assigned_to_name || apiMilestone.assignedTo,
    };
  }

  // ==========================================
  // HELPER METHODS FOR DATA DISPLAY
  // ==========================================

  /**
   * Get project/reference name for display
   */
  getProjectName(): string {
    const name =
      this.selectedItem?.name ||
      this.selectedItem?.data ||
      this.selectedItem?.title ||
      this.selectedItem?.bid_name ||
      this.selectedItem?.job_name ||
      this.selectedItem?.proposal_name ||
      this.selectedItem?.reference_number;

    if (name) {
      return name;
    }

    return this.model?.reference?.name || '';
  }

  /**
   * Get record/reference number for display
   */
  getRecordNumber(): string {
    const recordNum =
      this.selectedItem?.reference_number ||
      this.selectedItem?.ref_number ||
      this.selectedItem?.bid_number ||
      this.selectedItem?.job_number ||
      this.selectedItem?.proposal_number ||
      this.selectedItem?.number ||
      this.selectedItem?.id?.toString();

    return recordNum || this.model?.reference?.id?.toString() || '';
  }

  /**
   * Detail-page routes for module ids confirmed to appear on tasks' linked
   * records (real task data, not guessed). Deliberately not reusing
   * NotificationNavigationService's module map — its id 17 means Proposal
   * there, but MODULE_IDS (and real task data) confirm 17 is Warehouse.
   */
  private readonly LINKED_RECORD_ROUTES: Partial<Record<number, (id: number | string) => string>> = {
    [MODULE_IDS.PRODUCT]: (id) => `/products/product/${id}`,
    [MODULE_IDS.STAFF]: (id) => `/hr/staff/${id}`,
    [MODULE_IDS.VENDOR]: (id) => `/vendors/vendor/${id}`,
    [MODULE_IDS.CUSTOMER]: (id) => `/customers/customer/${id}`,
    [MODULE_IDS.BID]: (id) => `/bids/bid/${id}`,
    [MODULE_IDS.WAREHOUSE]: (id) => `/warehouses/warehouse/${id}`,
    [MODULE_IDS.JOB]: (id) => `/jobs/detail/${id}`,
    [MODULE_IDS.CHANGE_ORDER]: (id) => `/change-order/edit/${id}`,
  };

  /**
   * Route to the task's linked module record, or null when the module id is
   * unmapped or record data is unavailable — caller falls back to plain text
   * rather than guessing a URL. No permission check: the destination page
   * re-fetches the record from the backend and enforces access itself.
   */
  getRecordRoute(): string | null {
    const moduleId = this.model?.module?.id;
    const recordId = this.model?.reference?.id;

    if (!moduleId || !recordId) {
      return null;
    }

    const buildRoute = this.LINKED_RECORD_ROUTES[moduleId];
    return buildRoute ? buildRoute(recordId) : null;
  }

  /**
   * Get module label for display
   */
  /** Save in the flag popup: flags or unflags the task and stores the optional reason, in one request. */
  saveFlag({ isFlagged, reason }: { isFlagged: boolean; reason: string }): void {
    if (!this.taskID) { return; }
    this.backendApiService.postApi(`tasks/update-flag/${this.taskID}`, { is_flagged: isFlagged, reason }).subscribe({
      next: (response: any) => {
        this.model = {
          ...this.model,
          is_flagged: isFlagged ? 1 : 0,
          flag_text: isFlagged && reason ? reason : null,
          unflag_text: !isFlagged && reason ? reason : null,
        };
        this.toasterService.showSuccessMessage(response?.data?.message || (isFlagged ? 'Task flagged successfully!' : 'Flag removed successfully!'));
      },
    });
  }

  /** Saves the description edited inline with the pencil; `done` tells the editor whether it may close. */
  saveDescription({ value, done }: { value: string; done: (saved: boolean) => void }): void {
    if (!this.taskID) { done(false); return; }
    this.backendApiService.postApi(`tasks/update-description/${this.taskID}`, { description: value }).subscribe({
      next: (response: any) => {
        this.model = { ...this.model, description: value };
        this.sectionForm.patchValue({ description: value });
        this.toasterService.showSuccessMessage(response?.data?.message || 'Description updated successfully!');
        done(true);
      },
      error: () => done(false),
    });
  }

  /** Saves the optional reason typed in the flag popup (only called when the user typed one). */
  saveFlagReason(reason: string): void {
    if (!this.taskID) { return; }
    this.backendApiService.postApi(`tasks/update-flag/${this.taskID}`, { reason }).subscribe({
      next: (response: any) => {
        this.model = this.model?.is_flagged
          ? { ...this.model, flag_text: reason }
          : { ...this.model, unflag_text: reason };
        this.toasterService.showSuccessMessage(response?.data?.message || 'Reason saved successfully!');
      },
    });
  }

  getModuleLabel(): string {
    if (this.model?.module?.name) {
      return this.model.module.name;
    }

    if (this.modulesList) {
      const module = this.modulesList.find(
        (m: any) => m.id === this.model?.module?.id,
      );
      return module?.name || 'Project';
    }

    return 'Project';
  }

  /**
   * Get selected milestone object
   */
  getSelectedMilestone() {
    const milestoneId =
      this.model?.module_data_milestone_id || this.model?.milestone_id;
    return this.milestones.find((m: any) => m.id === milestoneId);
  }

  // Reactive property for task completion percentage
  completionPercentage: number = 0;

  /**
   * Calculate task progress based on status and checklist completion
   * Returns 100% if task is marked complete, otherwise checklist percentage
   */
  private calculateCompletionPercentage(): number {
    // Check if task is marked as complete via status
    if (this.isTaskComplete(this.model?.status_label)) {
      return 100;
    }

    // Calculate based on checklist completion
    const checklist = this.model?.check_list || [];
    if (!checklist.length) return 0;
    const completed = checklist.filter((item: any) => item.is_completed).length;
    return Math.round((completed / checklist.length) * 100);
  }

  /**
   * Check if a status indicates task completion
   */
  private isTaskComplete(status: any): boolean {
    if (!status) return false;

    // Check by status name or label (case-insensitive)
    const statusName = (
      status.name ||
      status.label ||
      status.status_name ||
      ''
    ).toLowerCase();

    // Common completion status names
    const completionKeywords = [
      'complete',
      'completed',
      'done',
      'finished',
      'closed',
    ];
    return completionKeywords.some((keyword) => statusName.includes(keyword));
  }

  /**
   * Get checklist item count
   */
  getChecklistCount(): number {
    return (this.model?.check_list || []).length;
  }

  /**
   * Get comments for the task
   * Updates taskComments array for comment count badge
   */
  getTaskComments(): void {
    if (this.taskID) {
      this.backendApiService
        .postApi(`tasks/get-comments/${this.taskID}`, '')
        .subscribe((response: any) => {
          this.taskComments = response?.data?.data || [];
        });
    }
  }

  /**
   * Handle comment added event from comments component
   * Refresh comment count in tab badge
   */
  handleCommentAdded(): void {
    this.getTaskComments();
  }

  /**
   * Get status text from stage
   */
  getStatusText(): string {
    return this.model?.stage?.name || this.model?.status_label?.name || 'New';
  }

  /**
   * Get status from model
   */
  getStatusFromForm(): string {
    if (this.model?.status_name) {
      return this.model.status_name.toLowerCase();
    }

    if (this.model?.status_label?.name) {
      return this.model.status_label.name.toLowerCase();
    }

    return 'new';
  }

  /**
   * Convert numeric priority to display text
   */
  getPriorityText(priority: number): string {
    const priorityMap: { [key: number]: string } = {
      1: 'low',
      2: 'medium',
      3: 'high',
      4: 'urgent',
    };
    return priorityMap[priority] || 'medium';
  }

  // ==========================================
  // EVENT HANDLERS FOR NEW UI COMPONENTS
  // ==========================================

  /**
   * Handle search query change
   */
  handleSearch(query: string) {
    this.searchQuery = query;
  }

  /**
   * Handle field changes from sidebar or other components
   */
  handleFieldChange(event: any): void {
    if (event.field && event.value !== undefined) {
      // Update model if needed
      if (this.model && this.model.hasOwnProperty(event.field)) {
        this.model[event.field] = event.value;
      }
    }
  }

  /**
   * Handle milestone selection
   */
  handleMilestoneSelect(milestone: any) {
    if (this.model) {
      this.model.milestone_id = milestone.id;
      this.model.module_data_milestone_id = milestone.id;
    }
  }

  /**
   * Handle checklist changes from task-checklists component
   * Recalculates progress when checklist items are toggled/added/removed
   */
  handleChecklistChange(checklists: any) {
    console.log('[VIEW-TASK-DETAIL] handleChecklistChange called', {
      hasModel: !!this.model,
      checklistsReceived: checklists?.length,
      currentProgress: this.completionPercentage,
    });

    if (this.model) {
      this.model.check_list = checklists;
      // Recalculate progress whenever checklists change
      const newProgress = this.calculateCompletionPercentage();
      console.log('[VIEW-TASK-DETAIL] Progress recalculated', {
        oldProgress: this.completionPercentage,
        newProgress: newProgress,
      });
      this.completionPercentage = newProgress;
    }
  }

  /**
   * Handle milestone removal
   */
  handleMilestoneRemove() {
    if (this.model) {
      this.model.milestone_id = null;
      this.model.module_data_milestone_id = null;
    }
  }

  /**
   * Handle edit description action
   */
  handleEditDescription() {
    // Switch to details tab or focus description field
    this.activeTabIndex = 0;
  }

  /**
   * Handle tab change
   */
  handleTabChange(event: any) {
    this.activeTabIndex = event.currentIndex || event;
  }

  /**
   * Handle assignee selection from inline UI
   */
  handleAssigneeSelected(user: any): void {
    this.addResponsiblePerson(user);
  }

  /**
   * Handle observer selection from inline UI
   */
  handleObserverSelected(user: any): void {
    this.addObserver(user);
  }

  /**
   * Handle participant selection from inline UI
   */
  handleParticipantSelected(user: any): void {
    this.addParticipant(user);
  }

  /**
   * Remove assignee by index
   */
  removeAssignee(index: number) {
    if (
      this.model?.responsible_persons &&
      this.model.responsible_persons[index]
    ) {
      this.deleteAssociation(this.model.responsible_persons[index]);
    }
  }

  /**
   * Remove observer by index
   */
  removeObserver(index: number) {
    if (this.model?.observers && this.model.observers[index]) {
      this.deleteAssociation(this.model.observers[index]);
    }
  }

  /**
   * Remove participant by index
   */
  removeParticipant(index: number) {
    if (this.model?.participants && this.model.participants[index]) {
      this.deleteAssociation(this.model.participants[index]);
    }
  }

  /**
   * Toggle favorite state
   * Uses /api/v1/user/favorites/add endpoint with module_id: 28 (Task module)
   */
  toggleFavorite(): void {
    if (this.isLocked) {
      return;
    }
    const previous = this.isFavorite;
    this.isFavorite = !this.isFavorite; // optimistic UI
    const payload = {
      reference_id: this.model?.id,
      module_id: 7, // Fixed value for Task module
      is_favorite: this.isFavorite ? 1 : 0,
    };
    this.backendApiService.postApi('user/favorites/add', payload).subscribe(
      (response: any) => {
        this.toasterService.showSuccessMessage(
          this.isFavorite
            ? 'Task added to favorites'
            : 'Task removed from favorites',
        );
        // keep local model in sync
        if (this.model) {
          this.model.is_favorite = this.isFavorite ? 1 : 0;
        }
      },
      (error: any) => {
        // revert on error
        this.isFavorite = previous;
        this.toasterService.showError('Failed to update favorite state');
      },
    );
  }

  /**
   * Set reminder for task
   */
  setReminder(): void {
    const taskName =
      this.model?.name || this.model?.title || `Task #${this.taskID}`;
    const dialogData: any = {
      reminderable_type: 'App\\Models\\Task',
      reminderable_id: this.taskID,
      entityName: taskName,
    };

    // Add location if present in task model
    if (this.model?.location) {
      dialogData.location = this.model.location;
    }

    const dialogRef = this.matDialog.open(AddReminderModalComponent, {
      panelClass: 'reminder-modal-panel',
      disableClose: false,
      autoFocus: true,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toasterService.showSuccessMessage('Reminder set successfully!');
      }
    });
  }

  /**
   * Handle comment component close request
   * @param data Optional data from close event
   */
  handleCommentClose(data: any): void {
    console.log('[VIEW-TASK-DETAIL] Comment close requested:', data);
    if (data === 'delete') {
      // Task was deleted from comments, close the view-task-detail dialog
      this.close();
    }
  }

  // ==========================================
  // TAG METHODS
  // ==========================================

  /**
   * Extract and transform tags from task model
   * @param model Task model from API
   * @returns Array of TaskTag objects
   */
  private extractTagsFromModel(model: any): TaskTag[] {
    if (!model) {
      // No model available → no tags
      return [];
    }

    // Try multiple possible locations for tags in the API response
    const tags = model.tags || model.relations?.tags || model.task_tags || [];

    // If no tags from API, return empty array (no static tags)
    if (!tags || tags.length === 0) {
      return [];
    }

    // Transform to TaskTag interface
    return tags.map((tag: any) => ({
      id: tag.id,
      name: tag.name || tag.tag_name || tag.label || tag.tag,
      color: tag.color || this.getDefaultTagColor(tag),
    }));
  }

  /**
   * Get static test tags for testing purposes
   * TODO: Remove this method once backend API is implemented
   * @returns Array of test TaskTag objects
   */
  private getStaticTestTags(): TaskTag[] {
    return [
      { id: 1, name: 'Urgent', color: 'red' },
      { id: 2, name: 'Bug Fix', color: 'orange' },
      { id: 3, name: 'Feature', color: 'blue' },
      { id: 4, name: 'In Progress', color: 'yellow' },
      { id: 5, name: 'Review', color: 'purple' },
    ];
  }

  /**
   * Assign default color if backend doesn't provide one
   * Uses tag ID to consistently assign same color to same tag
   * @param tag Tag object
   * @returns Color key string
   */
  private getDefaultTagColor(tag: any): string {
    const colors = [
      'blue',
      'green',
      'yellow',
      'red',
      'purple',
      'orange',
      'gray',
    ];
    const index = (tag.id || 0) % colors.length;
    return colors[index];
  }

  /**
   * Handle tag selector open request
   */
  handleTagSelectorOpen(): void {
    const dialogRef = this.dialog.open(TaskTagSelectorPopupComponent, {
      width: '500px',
      maxWidth: '500px',
      maxHeight: '80vh',
      panelClass: 'task-tag-selector-popup-dialog-container',
      data: {
        currentTags: this.taskTags,
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result) return;
      // Always add via user tags endpoint for Task Detail
      const selected = result.tag;
      if (!selected) return;
      this.addTagViaUserTags(selected);
    });
  }
  /**
   * Add a tag to the current task via user tags endpoint
   * module_id: 7 (Task Module ID), reference_id: current task id
   */
  private addTagViaUserTags(selectedTag: any): void {
    if (!this.model?.id) return;
    // Normalize inputs
    const name = (selectedTag?.name || selectedTag?.tag || '')
      .toString()
      .trim();
    if (!name) return;
    const color = this.resolveHex(selectedTag?.color);

    // Build form-data payload exactly as backend expects
    const payload = new FormData();
    payload.append('module_id', '7');
    payload.append('reference_id', String(this.model.id));
    payload.append('tag', name);
    payload.append('color', color);
    payload.append('is_system', '0');

    // Optimistic UI
    const optimistic = { id: undefined, name, color } as any;
    const prev = [...this.taskTags];
    this.taskTags = [...this.taskTags, optimistic];

    this.backendApiService.postApi('user/tags', payload).subscribe(
      (response: any) => {
        const created = response?.data || null;
        if (created) {
          const idx = this.taskTags.indexOf(optimistic);
          if (idx > -1) {
            this.taskTags[idx] = {
              id: created.id,
              name: created.name || created.tag || name,
              color: created.color || color,
            } as any;
            this.taskTags = [...this.taskTags];
          }
        }
        this.toasterService.showSuccessMessage('Tag added successfully');
        this.getTaskByID(false);
      },
      () => {
        this.taskTags = prev; // rollback
        this.toasterService.showError('Failed to add tag');
      },
    );
  }

  /** Map dialog color keys to hex; keep hex as-is */
  private resolveHex(color: string): string {
    if (!color) return '#6b7280';
    if (typeof color === 'string' && color.startsWith('#')) return color;
    const map: any = {
      blue: '#3b82f6',
      green: '#22c55e',
      yellow: '#eab308',
      red: '#ef4444',
      purple: '#a855f7',
      orange: '#f97316',
      gray: '#6b7280',
    };
    return map[(color || '').toString().toLowerCase()] || '#6b7280';
  }

  /**
   * Add existing tag to task
   */
  private addExistingTag(tag: TaskTag): void {
    this.backendApiService
      .postApi(`tasks/${this.model?.id}/tags`, {
        tag_id: tag.id,
      })
      .subscribe(
        (response: any) => {
          this.toasterService.showSuccessMessage('Tag added successfully');
          this.getTaskByID(false); // Reload task to get updated tags
        },
        (error) => {
          this.toasterService.showError('Failed to add tag');
          console.error('Error adding tag:', error);
        },
      );
  }

  /**
   * Create new tag and add to task
   */
  private createAndAddTag(tag: TaskTag): void {
    // First create the tag
    this.backendApiService
      .postApi('tags', {
        name: tag.name,
        color: tag.color,
      })
      .subscribe(
        (response: any) => {
          const createdTag = response.data;
          // Then add it to the task
          this.addExistingTag(createdTag);
        },
        (error) => {
          this.toasterService.showError('Failed to create tag');
          console.error('Error creating tag:', error);
        },
      );
  }

  /**
   * Remove tag from task
   * Uses DELETE /api/v1/user/tags/{id} endpoint
   */
  handleTagRemove(index: number): void {
    const tag = this.taskTags[index];
    if (!tag || !tag.id) return;

    // Optimistic UI - remove tag immediately
    const previousTags = [...this.taskTags];
    this.taskTags = this.taskTags.filter((_, i) => i !== index);

    this.backendApiService.deleteApi(`user/tags/${tag.id}`).subscribe(
      (response: any) => {
        this.toasterService.showSuccessMessage('Tag removed successfully');
        this.getTaskByID(false); // Reload task to get updated tags
      },
      (error) => {
        // Rollback on error
        this.taskTags = previousTags;
        this.toasterService.showError('Failed to remove tag');
        console.error('Error removing tag:', error);
      },
    );
  }

  /**
   * Handle tags updated from dropdown in View mode
   * Makes immediate API calls for added/removed tags
   */
  handleTagsUpdatedInViewMode(updatedTags: TaskTag[]): void {
    if (!this.model?.id) return;

    // Find tags to add (in updatedTags but not in current taskTags)
    const tagsToAdd = updatedTags.filter(
      (newTag) =>
        !this.taskTags.some(
          (existingTag) =>
            existingTag.id === newTag.id || existingTag.name === newTag.name,
        ),
    );

    // Find tags to remove (in current taskTags but not in updatedTags)
    const tagsToRemove = this.taskTags.filter(
      (existingTag) =>
        !updatedTags.some(
          (newTag) =>
            newTag.id === existingTag.id || newTag.name === existingTag.name,
        ),
    );

    // Optimistic UI update
    const previousTags = [...this.taskTags];
    this.taskTags = [...updatedTags];

    // Process additions and deletions
    const apiCalls: any[] = [];

    // Add new tags
    tagsToAdd.forEach((tag) => {
      const payload = new FormData();
      payload.append('module_id', '7');
      payload.append('reference_id', String(this.model.id));
      payload.append('tag', tag.name);
      payload.append('color', this.resolveHex(tag.color));
      payload.append('is_system', '0');

      apiCalls.push(this.backendApiService.postApi('user/tags', payload));
    });

    // Remove old tags
    tagsToRemove.forEach((tag) => {
      if (tag.id) {
        apiCalls.push(this.backendApiService.deleteApi(`user/tags/${tag.id}`));
      }
    });

    // Execute all API calls
    if (apiCalls.length > 0) {
      Promise.all(apiCalls.map((call) => call.toPromise()))
        .then(() => {
          this.toasterService.showSuccessMessage('Tags updated successfully');
          this.getTaskByID(false); // Reload to sync with backend
        })
        .catch((error) => {
          // Rollback on error
          this.taskTags = previousTags;
          this.toasterService.showError('Failed to update tags');
          console.error('Error updating tags:', error);
        });
    }
  }

  // ==========================================
  // HISTORY METHODS
  // ==========================================
}
